import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { MemberStatusBadge } from '../member/memberList/MemberListStyled';

// ============================================
// Layout tokens (디자인 핸드오프)
// ============================================
const GRID_TEMPLATE = '64px minmax(240px,1fr) 200px 148px 132px 84px 140px 132px 104px';
const MIN_WIDTH = 1244;

// ============================================
// Styled Components
// ============================================
const Card = styled(Box)({
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
  marginTop: '10px',
});

const ScrollArea = styled(Box)({
  overflowX: 'auto',
});

const HeaderRow = styled(Box)({
  display: 'grid',
  gridTemplateColumns: GRID_TEMPLATE,
  minWidth: `${MIN_WIDTH}px`,
  alignItems: 'center',
  height: '52px',
  padding: '0 20px',
  // 목록이 길어져도 컬럼명이 화면에 남게 한다.
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: '#fafbfc',
  borderBottom: '1px solid #e5e7eb',
  fontSize: '13px',
  fontWeight: 600,
  color: '#4b5563',
  textAlign: 'center',
});

const MainRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'expiring' && prop !== 'clickable',
})(({ expiring, clickable }) => ({
  display: 'grid',
  gridTemplateColumns: GRID_TEMPLATE,
  minWidth: `${MIN_WIDTH}px`,
  alignItems: 'center',
  minHeight: '56px',
  padding: '0 20px',
  borderBottom: '1px solid #f1f2f4',
  fontSize: '14px',
  textAlign: 'center',
  // 만료임박은 행 전체를 분홍으로 칠한다 — 액센트 바만으로는 눈에 안 들어온다는
  // 사용자 지적으로 되돌렸다 (2026-08-27).
  background: expiring ? '#FFECEB' : '#ffffff',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'background 0.12s',
}));

const SubRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'expiring',
})(({ expiring }) => ({
  display: 'grid',
  gridTemplateColumns: GRID_TEMPLATE,
  minWidth: `${MIN_WIDTH}px`,
  alignItems: 'center',
  minHeight: '46px',
  padding: '0 20px',
  borderBottom: '1px solid #f1f2f4',
  fontSize: '14px',
  textAlign: 'center',
  background: expiring ? '#FFF4F3' : '#f9fafb',
}));

const NumCell = styled(Box)({
  color: '#6b7280',
  fontVariantNumeric: 'tabular-nums',
});

const BranchCell = styled(Box)({
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
});

// 지점명: 사용자 요청으로 링크가 아닌 검은색 텍스트
const BranchText = styled('span')({
  color: '#1f2937',
});

const NameLink = styled('span')({
  fontWeight: 600,
  color: '#1f2937',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
});

const CountCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
});

const CountText = styled('span', {
  shouldForwardProp: prop => prop !== 'expandable',
})(({ expandable }) => ({
  fontVariantNumeric: 'tabular-nums',
  fontWeight: expandable ? 700 : 500,
}));

const Chevron = styled('span', {
  shouldForwardProp: prop => prop !== 'open',
})(({ open }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  borderRadius: '6px',
  background: '#eef1f5',
  color: '#4b5563',
  fontSize: '11px',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.15s',
}));

const SubMarker = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  color: '#9aa1ab',
  fontSize: '13px',
  paddingLeft: '8px',
});

const SubLabel = styled('span')({
  color: '#9aa1ab',
  fontSize: '13px',
});

const EmptyBox = styled(Box)({
  padding: '24px',
  textAlign: 'center',
});

// 기존 i18n 키 재사용(EN 로케일 번역 유지). '지점 명'/'회원상태'/'만료예정 상품'은 한글 키.
const HEADERS = [
  'Number',
  '지점 명',
  '만료예정 상품',
  '회원상태',
  'Name',
  'State',
  'Attend Date',
  'Attend Start Time',
  'Attend Count',
];

// 만료예정 상품 셀: 첫 상품만 노출하고 나머지는 "외 N건"으로 접는다. 전체는 셀 title 툴팁으로 확인.
const formatExpiringProduct = ({ productName, expireDate }, dateFormat) =>
  expireDate ? `${productName} (~${dayjs(expireDate).format(dateFormat)})` : productName;

const summarizeExpiringProducts = products => {
  if (!products || products.length === 0) {
    return { head: '-', restCount: 0, tooltip: '' };
  }
  const [first, ...rest] = products;

  return {
    head: formatExpiringProduct(first, 'MM-DD'),
    restCount: rest.length,
    tooltip: products.map(p => formatExpiringProduct(p, 'YYYY-MM-DD')).join('\n'),
  };
};

// ============================================
// Main Component
// ============================================
const AttendHistoryListTable = ({ data = [], loading = false, page = 1, pageSize = 10 }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState({});

  // 검색/페이지/필터로 데이터가 바뀌면 펼침 상태 초기화(stale 펼침 방지)
  useEffect(() => {
    setExpanded({});
  }, [data]);

  const toggle = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const goMemberDetail = (e, memberId) => {
    e.stopPropagation();
    if (memberId) {
      navigate(`/member/${memberId}?tab=three`);
    }
  };

  const isEmpty = data.length === 0;

  return (
    <Card>
      <ScrollArea>
        {/* header */}
        <HeaderRow>
          <div>{t(HEADERS[0])}</div>
          <div>{t(HEADERS[1])}</div>
          <div>{t(HEADERS[2])}</div>
          <div>{t(HEADERS[3])}</div>
          <div>{t(HEADERS[4])}</div>
          <div>{t(HEADERS[5])}</div>
          <div>{t(HEADERS[6])}</div>
          <div>{t(HEADERS[7])}</div>
          <div>{t(HEADERS[8])}</div>
        </HeaderRow>

        {isEmpty ? (
          <EmptyBox>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '16px' }}>
              {loading ? t('검색중...') : t('내역이 없습니다.')}
            </Typography>
          </EmptyBox>
        ) : (
          data.map((item, index) => {
            const entries = item.entries || [];
            const count = entries.length;
            const expandable = count > 1;
            const expiring = item.memberType === '만료임박';
            const groupKey = `${item.memberId}-${item.writeDate}`;
            const isOpen = !!expanded[groupKey];
            const dateText = item.writeDate ? dayjs(item.writeDate).format('YYYY-MM-DD') : '-';
            const attendType = item.attendType || '입실';
            const expiringProducts = summarizeExpiringProducts(item.expiringProducts);

            return (
              <React.Fragment key={groupKey || index}>
                <MainRow
                  expiring={expiring}
                  clickable={expandable}
                  onClick={expandable ? () => toggle(groupKey) : undefined}
                >
                  <NumCell>{(page - 1) * pageSize + index + 1}</NumCell>
                  <BranchCell>
                    <BranchText>{item.branchName || '-'}</BranchText>
                  </BranchCell>
                  <Box
                    title={expiringProducts.tooltip}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', px: '4px' }}
                  >
                    <Box
                      component="span"
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#374151' }}
                    >
                      {expiringProducts.head}
                    </Box>
                    {expiringProducts.restCount > 0 && (
                      <Box component="span" sx={{ flexShrink: 0, color: '#9aa1ab', fontSize: '13px' }}>
                        외 {expiringProducts.restCount}건
                      </Box>
                    )}
                  </Box>
                  <Box>
                    {item.memberType ? (
                      <MemberStatusBadge memberType={item.memberType}>
                        {item.memberType}
                      </MemberStatusBadge>
                    ) : (
                      '-'
                    )}
                  </Box>
                  <Box>
                    <NameLink onClick={e => goMemberDetail(e, item.memberId)}>
                      {item.name || '-'}
                    </NameLink>
                  </Box>
                  <Box component="span" sx={{
                    display: 'inline-block', padding: '2px 9px', borderRadius: '10px',
                    fontSize: '12.5px', fontWeight: 700,
                    color: attendType === '입실' ? '#0D9298' : '#6B7280',
                    background: attendType === '입실' ? 'rgba(13,146,152,0.10)' : '#EEF1F3',
                  }}>{attendType}</Box>
                  <Box sx={{ color: '#4b5563', fontVariantNumeric: 'tabular-nums' }}>{dateText}</Box>
                  <Box sx={{ color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
                    {entries[0] || '-'}
                  </Box>
                  <CountCell>
                    <CountText expandable={expandable}>{count}</CountText>
                    {expandable && <Chevron open={isOpen}>▼</Chevron>}
                  </CountCell>
                </MainRow>

                {expandable &&
                  isOpen &&
                  entries.slice(1).map((time, subIdx) => (
                    <SubRow key={`${groupKey}-s${subIdx}`} expiring={expiring}>
                      <Box />
                      <BranchCell>
                        <SubMarker>↳</SubMarker>
                        <SubLabel>{t('추가 입장')}</SubLabel>
                      </BranchCell>
                      <Box />
                      <Box />
                      <Box />
                      <Box component="span" sx={{
                    display: 'inline-block', padding: '2px 9px', borderRadius: '10px',
                    fontSize: '12.5px', fontWeight: 700,
                    color: attendType === '입실' ? '#0D9298' : '#6B7280',
                    background: attendType === '입실' ? 'rgba(13,146,152,0.10)' : '#EEF1F3',
                  }}>{attendType}</Box>
                      <Box sx={{ color: '#4b5563', fontVariantNumeric: 'tabular-nums' }}>
                        {dateText}
                      </Box>
                      <Box sx={{ color: '#111827', fontVariantNumeric: 'tabular-nums' }}>{time}</Box>
                      <Box />
                    </SubRow>
                  ))}
              </React.Fragment>
            );
          })
        )}
      </ScrollArea>
    </Card>
  );
};

export default AttendHistoryListTable;
